import { useState, useEffect } from "react"
import Menu from "aldoo-ra/Menu"
import { GetParam } from "aldoo-ra/Admin/ParamIO/api"
import { GetCMSContent } from "aldoo-ra/CMS/api"
import SiteTheme from "aldoo-ra/SiteTheme"
import { SunIcon } from "@heroicons/react/24/outline"

const PARAM_ADDRESS = "SITE_THEME_USER_AVAILABLE"

export default function ThemeSwitcher({
  showMenuMode = "hover",
  menuPosition = "bottom",
  menuAlign = "right",
}) {
  const [isClient, setIsClient] = useState(false)
  const [themes, setThemes] = useState([])
  const [currentThemeId, setCurrentThemeId] = useState(null)
  const [currentThemeName, setCurrentThemeName] = useState(null)

  useEffect(() => { setIsClient(true) }, [])

  useEffect(() => {
    if (!isClient) return
    load()

    const handler = (theme) => {
      setCurrentThemeId(theme?._id || null)
      setCurrentThemeName(theme?.name || null)
    }
    SiteTheme.onThemeChange(handler)
    return () => SiteTheme.offThemeChange(handler)
  }, [isClient])

  async function load() {
    try {
      const [param, themesRes] = await Promise.all([
        GetParam(PARAM_ADDRESS).catch(() => null),
        // itemsPerPage: the CMS default page size is 10 — fetch the full list
        GetCMSContent({ match: { "content.type": "SiteTheme", deleted: { $ne: true } }, itemsPerPage: 500 }),
      ])

      const allThemes = (themesRes?.data || []).map((t) => ({
        id: t._id,
        name: t.content.name,
      }))

      const rawValue = param?.address ? param.value : null
      const availableIds = Array.isArray(rawValue)
        ? rawValue
        : rawValue
        ? JSON.parse(rawValue)
        : []

      const available = availableIds
        .map((id) => allThemes.find((t) => t.id === id))
        .filter(Boolean)

      setThemes(available)

      // getTheme() returns { ...content, _id } from getThemeByIdFromAPI
      const current = SiteTheme.getTheme()
      if (current) {
        setCurrentThemeId(current._id || null)
        setCurrentThemeName(current.name || null)
      }
    } catch (err) {
      console.error("ThemeSwitcher load error:", err)
    }
  }

  if (!isClient || themes.length < 2) return null

  const menu = {
    label: currentThemeName || "Theme",
    items: themes.map((theme) => ({
      options: {
        action: () => SiteTheme.setTheme(theme.id),
      },
      label: theme.name,
    })),
  }

  return (
    <div>
      <Menu
        menuData={menu}
        hideOnItemClick
        renderAs="vlist.menu"
        maxItemsPerColumn={10}
        menuAlign={menuAlign}
        showMenuMode={showMenuMode}
        menuPosition={menuPosition}
        styleOverrides={{
          submenu:
            "absolute top-full mt-2 py-1.5 bg-paper shadow-lg border border-bordered rounded-2xl z-50 min-w-[160px] overflow-hidden",
          menu: "list-none p-0 m-0 w-full",
          menu_item: "flex flex-col p-0 m-0 cursor-pointer w-full",
          menu_column: "w-full",
          root_item: "flex items-center cursor-pointer",
        }}
        menuItemRender={(item) => {
          const isActive = item.label === currentThemeName
          return (
            <div className={`px-4 py-3 text-sm cursor-pointer w-full text-left transition ${isActive ? "text-primary font-semibold" : "text-text hover:bg-primary/10 hover:text-primary"}`}>
              {item.label}
            </div>
          )
        }}
        rootItemRender={() => (
          <div className="h-10 flex items-center gap-1.5 text-text hover:text-primary rounded-lg px-3 transition text-sm font-medium cursor-pointer">
            <SunIcon className="w-4 h-4 flex-shrink-0" />
            <span>{currentThemeName || "Theme"}</span>
          </div>
        )}
      />
    </div>
  )
}
